import { HttpStatusCode } from 'axios';
import classNames from 'classnames';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Copy,
  Info,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeVariantProps } from '@/Domain/components/Shared/Badge/Badge';
import { PrettyJsonViewer } from '@/Domain/components/Shared/JsonViewier/JsonViewier';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  data: any; // Data from external API - just to display the results
  httpCode?: HttpStatusCode;
  bashCurlCommand?: string;
};

const getBadgeConfig = (
  statusCode: number | undefined,
): { variant: BadgeVariantProps['variant']; label: string; icon: LucideIcon } | null => {
  if (!statusCode) {
    return null;
  }

  if (statusCode >= 400 && statusCode <= 511) {
    return {
      variant: 'error',
      label: `${statusCode} ${HttpStatusCode[statusCode]}`,
      icon: TriangleAlert,
    };
  }

  if (statusCode >= 200 && statusCode < 300) {
    return {
      variant: 'success',
      label: `${statusCode} ${HttpStatusCode[statusCode]}`,
      icon: CircleCheck,
    };
  }

  return null;
};

const getMessageField = (value: unknown, field: 'error' | 'info'): string | null => {
  if (typeof value !== 'object' || value === null) return null;
  const message = (value as Record<string, unknown>)[field];
  return typeof message === 'string' ? message : null;
};

type IncompleteCallResultProps = {
  variant: BadgeVariantProps['variant'];
  icon: LucideIcon;
  label: string;
  message: string;
  testId: string;
};

const IncompleteCallResult = ({
  variant,
  icon: Icon,
  label,
  message,
  testId,
}: IncompleteCallResultProps) => (
  <div className="border rounded-lg w-full p-4 space-y-3">
    <Badge variant={variant}>
      <Icon className="size-3.5" />
      {label}
    </Badge>
    <p className="text-sm text-foreground" data-testid={testId}>
      {message}
    </p>
  </div>
);

export const Response = ({ data, httpCode, bashCurlCommand }: Props) => {
  const { t } = useTranslation('actions_page');
  const badge = getBadgeConfig(httpCode);
  const BadgeIcon = badge?.icon;
  const [curlExpanded, setCurlExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!bashCurlCommand) return;
    await navigator.clipboard.writeText(bashCurlCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // The call never produced an HTTP status: show only the friendly message, never the raw
  // object (older stored payloads may still carry a full_trace). `info` is a run-condition
  // gate declining to call, which is the guard working rather than a failure.
  if (!httpCode) {
    const notRunMessage = getMessageField(data, 'info');
    if (notRunMessage) {
      return (
        <IncompleteCallResult
          variant="info"
          icon={Info}
          label={t('sheet.form.test_response.not_run')}
          message={notRunMessage}
          testId="test-response-info-message"
        />
      );
    }

    const errorMessage = getMessageField(data, 'error');
    if (errorMessage) {
      return (
        <IncompleteCallResult
          variant="error"
          icon={TriangleAlert}
          label={t('sheet.form.test_response.request_failed')}
          message={errorMessage}
          testId="test-response-error-message"
        />
      );
    }
  }

  if (data) {
    return (
      <div className="space-y-3">
        <div className={classNames('relative border rounded-lg w-full flex overflow-y-auto')}>
          <section className="flex justify-between items-center absolute top-3 right-3">
            {badge && BadgeIcon && (
              <Badge variant={badge.variant}>
                <BadgeIcon className="size-3.5" />
                {badge.label}
              </Badge>
            )}
          </section>

          <PrettyJsonViewer data={data} />
        </div>

        {bashCurlCommand && (
          <div className="border rounded-lg overflow-hidden">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setCurlExpanded(!curlExpanded)}
                    className="flex items-center gap-1.5 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors"
                  >
                    {curlExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    {curlExpanded
                      ? t('sheet.form.test_response.curl.hide')
                      : t('sheet.form.test_response.curl.show')}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {t('sheet.form.test_response.curl.tooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {curlExpanded && (
              <div className="relative border-t">
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`absolute top-2 right-2 p-1.5 rounded hover:bg-accent text-foreground-subtle transition-colors ${
                    copied ? 'pointer-events-none' : ''
                  }`}
                  title={
                    copied
                      ? t('sheet.form.test_response.curl.copied')
                      : t('sheet.form.test_response.curl.copy')
                  }
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <pre className="p-3 pr-10 text-xs bg-muted overflow-x-auto whitespace-pre-wrap break-all font-mono">
                  {bashCurlCommand}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};
